import { Button, Stack, useDisclosure } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AlertDialog } from '../../components/alert-dialog';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import { UserPreferenceKey } from '../types';
import {
  enqueuePageOrderSave,
  writePageOrderToCache,
} from '../utils/page-order-queue';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.PageOrder,
  label: 'Analytics Tabs',
};

export default function PageOrderEditor({
  affectedScope,
}: PreferenceEditorProps) {
  const queryClient = useQueryClient();
  const confirm = useDisclosure();

  const { mutate: resetOrder, isPending } = useMutation({
    mutationFn: () => {
      // Sync cache write so sibling writers (e.g. a delete cleanup that races
      // with reset) read the reset value, not the pre-reset snapshot.
      writePageOrderToCache(queryClient, affectedScope, []);
      return enqueuePageOrderSave(affectedScope, []);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Analytics Tabs"
        indicateDiff={false}
        tooltip="Reset your tab order back to the default sequence"
      />
      <Stack mt={3} spacing={3}>
        <Button
          data-testid="reset-analytics-tab-order"
          className={styles.resetTabOrderButton}
          size="sm"
          variant="outline"
          colorScheme="gray"
          color="gray"
          w="fit-content"
          isLoading={isPending}
          isDisabled={isPending}
          onClick={confirm.onOpen}
        >
          Reset to default
        </Button>
      </Stack>
      <AlertDialog
        skipDefaultDisclosure
        alertType="confirm"
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        headerText="Reset Analytics Tabs"
        bodyContent={
          <span>
            Tabs will return to their default order. Custom pages stay, but move
            into the pages menu.
          </span>
        }
        footerAcceptText="Reset"
        footerAcceptIsDestructive
        cancelColorScheme="gray"
        onAccept={() => resetOrder()}
      />
    </>
  );
}
